import React, { useState } from 'react';
import { Users, FileText, Calendar, User, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

const TraspasoPropiedad: React.FC = () => {
  const [formData, setFormData] = useState({
    // Datos del vehículo
    placa: '',
    numeroTarjeta: '',
    
    // Datos del propietario actual
    propietarioActual: {
      tipoDocumento: '',
      numeroDocumento: '',
      nombre: '',
      apellido: '',
      telefono: '',
      email: ''
    },
    
    // Datos del nuevo propietario
    nuevoPropietario: {
      tipoDocumento: '',
      numeroDocumento: '',
      nombre: '',
      apellido: '',
      telefono: '',
      email: '',
      direccion: ''
    },
    
    // Datos del traspaso
    fechaTraspaso: '',
    valorTraspaso: '',
    motivoTraspaso: '',
    observaciones: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('propietarioActual.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        propietarioActual: {
          ...prev.propietarioActual,
          [field]: value
        }
      }));
    } else if (name.startsWith('nuevoPropietario.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        nuevoPropietario: {
          ...prev.nuevoPropietario,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.placa) {
      toast.error('Por favor ingresa la placa del vehículo');
      return;
    }
    
    if (!formData.nuevoPropietario.tipoDocumento || !formData.nuevoPropietario.numeroDocumento) {
      toast.error('Por favor completa los datos del nuevo propietario');
      return;
    }
    
    toast.success('Traspaso de propiedad procesado exitosamente');
    console.log('Datos del traspaso:', formData);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Traspaso de Propiedad</h1>
        <p className="text-gray-600">Transfiere la propiedad de un vehículo a un nuevo propietario</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Datos del Vehículo */}
        <div className="card p-6">
          <div className="flex items-center space-x-2 mb-6">
            <FileText className="w-6 h-6 text-primary-600" />
            <h2 className="text-xl font-semibold text-gray-900">Datos del Vehículo</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Placa del Vehículo *</label>
              <input
                type="text"
                name="placa"
                value={formData.placa}
                onChange={handleInputChange}
                className="input"
                placeholder="Ej: ABC123"
                required
              />
            </div>
            
            <div>
              <label className="label">Número de Tarjeta de Propiedad</label>
              <input
                type="text"
                name="numeroTarjeta"
                value={formData.numeroTarjeta}
                onChange={handleInputChange}
                className="input"
                placeholder="Número de tarjeta"
              />
            </div>
          </div>
        </div>

        {/* Propietario Actual */}
        <div className="card p-6">
          <div className="flex items-center space-x-2 mb-6">
            <User className="w-6 h-6 text-primary-600" />
            <h2 className="text-xl font-semibold text-gray-900">Propietario Actual</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="label">Tipo de Documento</label>
              <select
                name="propietarioActual.tipoDocumento"
                value={formData.propietarioActual.tipoDocumento}
                onChange={handleInputChange}
                className="input"
              >
                <option value="">Seleccione tipo</option>
                <option value="CC">Cédula de Ciudadanía</option>
                <option value="CE">Cédula de Extranjería</option>
                <option value="NIT">NIT</option>
              </select>
            </div>
            
            <div>
              <label className="label">Número de Documento</label>
              <input
                type="text"
                name="propietarioActual.numeroDocumento"
                value={formData.propietarioActual.numeroDocumento}
                onChange={handleInputChange}
                className="input"
                placeholder="Número de documento"
              />
            </div>
            
            <div>
              <label className="label">Nombre</label>
              <input
                type="text"
                name="propietarioActual.nombre"
                value={formData.propietarioActual.nombre}
                onChange={handleInputChange}
                className="input"
                placeholder="Nombre"
              />
            </div>
            
            <div>
              <label className="label">Apellido</label>
              <input
                type="text"
                name="propietarioActual.apellido"
                value={formData.propietarioActual.apellido}
                onChange={handleInputChange}
                className="input"
                placeholder="Apellido"
              />
            </div>
            
            <div>
              <label className="label">Teléfono</label>
              <input
                type="tel"
                name="propietarioActual.telefono"
                value={formData.propietarioActual.telefono}
                onChange={handleInputChange}
                className="input"
                placeholder="Teléfono"
              />
            </div>
            
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                name="propietarioActual.email"
                value={formData.propietarioActual.email}
                onChange={handleInputChange}
                className="input"
                placeholder="Email"
              />
            </div>
          </div>
        </div>

        {/* Flecha de Transferencia */}
        <div className="flex justify-center">
          <div className="flex items-center space-x-2 text-primary-600">
            <ArrowRight className="w-6 h-6" />
            <span className="font-medium">Transferir Propiedad</span>
            <ArrowRight className="w-6 h-6" />
          </div>
        </div>

        {/* Nuevo Propietario */}
        <div className="card p-6">
          <div className="flex items-center space-x-2 mb-6">
            <Users className="w-6 h-6 text-primary-600" />
            <h2 className="text-xl font-semibold text-gray-900">Nuevo Propietario</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="label">Tipo de Documento *</label>
              <select
                name="nuevoPropietario.tipoDocumento"
                value={formData.nuevoPropietario.tipoDocumento}
                onChange={handleInputChange}
                className="input"
                required
              >
                <option value="">Seleccione tipo</option>
                <option value="CC">Cédula de Ciudadanía</option>
                <option value="CE">Cédula de Extranjería</option>
                <option value="NIT">NIT</option>
                <option value="TI">Tarjeta de Identidad</option>
              </select>
            </div>
            
            <div>
              <label className="label">Número de Documento *</label>
              <input
                type="text"
                name="nuevoPropietario.numeroDocumento"
                value={formData.nuevoPropietario.numeroDocumento}
                onChange={handleInputChange}
                className="input"
                placeholder="Número de documento"
                required
              />
            </div>
            
            <div>
              <label className="label">Nombre *</label>
              <input
                type="text"
                name="nuevoPropietario.nombre"
                value={formData.nuevoPropietario.nombre}
                onChange={handleInputChange}
                className="input"
                placeholder="Nombre"
                required
              />
            </div>
            
            <div>
              <label className="label">Apellido *</label>
              <input
                type="text"
                name="nuevoPropietario.apellido"
                value={formData.nuevoPropietario.apellido}
                onChange={handleInputChange}
                className="input"
                placeholder="Apellido"
                required
              />
            </div>
            
            <div>
              <label className="label">Teléfono</label>
              <input
                type="tel"
                name="nuevoPropietario.telefono"
                value={formData.nuevoPropietario.telefono}
                onChange={handleInputChange}
                className="input"
                placeholder="Teléfono"
              />
            </div>
            
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                name="nuevoPropietario.email"
                value={formData.nuevoPropietario.email}
                onChange={handleInputChange}
                className="input"
                placeholder="Email"
              />
            </div>
            
            <div className="md:col-span-2 lg:col-span-3">
              <label className="label">Dirección</label>
              <input
                type="text"
                name="nuevoPropietario.direccion"
                value={formData.nuevoPropietario.direccion}
                onChange={handleInputChange}
                className="input"
                placeholder="Dirección completa"
              />
            </div>
          </div>
        </div>

        {/* Datos del Traspaso */}
        <div className="card p-6">
          <div className="flex items-center space-x-2 mb-6">
            <Calendar className="w-6 h-6 text-primary-600" />
            <h2 className="text-xl font-semibold text-gray-900">Información del Traspaso</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Fecha del Traspaso</label>
              <input
                type="date"
                name="fechaTraspaso"
                value={formData.fechaTraspaso}
                onChange={handleInputChange}
                className="input"
              />
            </div>
            
            <div>
              <label className="label">Valor del Traspaso</label>
              <input
                type="number"
                name="valorTraspaso"
                value={formData.valorTraspaso}
                onChange={handleInputChange}
                className="input"
                placeholder="Valor en pesos"
                min="0"
              />
            </div>
            
            <div>
              <label className="label">Motivo del Traspaso</label>
              <select
                name="motivoTraspaso"
                value={formData.motivoTraspaso}
                onChange={handleInputChange}
                className="input"
              >
                <option value="">Seleccione motivo</option>
                <option value="venta">Venta</option>
                <option value="donacion">Donación</option>
                <option value="herencia">Herencia</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            
            <div>
              <label className="label">Observaciones</label>
              <textarea
                name="observaciones"
                value={formData.observaciones}
                onChange={handleInputChange}
                className="input"
                rows={3}
                placeholder="Observaciones adicionales"
              />
            </div>
          </div>
        </div>

        {/* Botones de Acción */}
        <div className="flex justify-end space-x-4">
          <button type="button" className="btn btn-secondary">
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary">
            <ArrowRight className="w-4 h-4 mr-2" />
            Procesar Traspaso
          </button>
        </div>
      </form>
    </div>
  );
};

export default TraspasoPropiedad;
